import { lazy, Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ProtectedRoute } from '@/client/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import CreateChatbotV3 from './modules/workflows/pages/CreateChatbotV3';
import EditChatbotV4 from './modules/workflows/pages/EditChatbotV4';
import { TasksHomePage } from './modules/workflows/pages/TasksHomePage';
import { ClientApp } from './client/ClientApp';
import { LoginPage } from './client/pages/LoginPage';
import { OrgsPage } from './client/pages/OrgsPage';
import { UnifiedDashboardLayout } from './components/shared/UnifiedDashboardLayout';
import { ReportsPage as ClientReportsPage } from './client/pages/ReportsPage';
import { TeamPage as ClientTeamPage } from './client/pages/TeamPage';
import { WorkflowsRouteWrapper } from './client/components/WorkflowsRouteWrapper';
import { TasksPage } from './client/pages/TasksPage';
import { TaskGroupViewPage } from './client/pages/TaskGroupViewPage';
import { TaskGroupDataPage } from './client/pages/TaskGroupDataPage';
import { WorkflowPage } from './client/pages/WorkflowPage';
import { ProfilingPage } from './client/pages/ProfilingPage';
import { ProfileListPage } from './client/pages/ProfileListPage';
import { ProfileDetailPage } from './client/pages/ProfileDetailPage';
import { ProfilingSettingsPage } from './client/pages/ProfilingSettingsPage';
import { FeatureGuard } from './client/components/auth/FeatureGuard';
import { AdminGuard } from './client/components/auth/AdminGuard';
import { DeliveryLogsPage } from './client/pages/DeliveryLogsPage';
import { BulkQrCodesPage } from '@/modules/qrcodes';
import { MetabaseReportsPage } from '@/modules/metabase-reports';
import { InstantFlowPage } from '@/modules/instant-flow';
import { MasterDataPage } from './modules/master-data';
import { DefaultFeatureRedirect } from './client/components/DefaultFeatureRedirect';
import { LegacyClientRedirect } from './client/components/LegacyClientRedirect';
import { LegacyAIRedirect } from '@/modules/workflows/ai/LegacyAIRedirect';
import { GuidePage } from '@/modules/guide';
import {
  aboutPage,
  industryPage,
  instantTasksPage,
  marketingPage,
  privacyPage,
  teamPage,
  termsPage,
} from '@/marketingPages';

const MarketingLandingPage = marketingPage.Page;
const IndustryLandingPage = industryPage.Page;
const InstantTasksLandingPage = instantTasksPage.Page;
const TermsPage = termsPage.Page;
const AboutPage = aboutPage.Page;
const PrivacyPage = privacyPage.Page;
const TeamPage = teamPage.Page;
const SlideshowPage = lazy(() => import(/* webpackChunkName: "slideshow" */'@/pages/SlideshowPage'));
const CalendarPage = lazy(() => import(/* webpackChunkName: "calendar" */'@/pages/CalendarPage'));
const TaskApprovalPage = lazy(() => import('@/pages/TaskApprovalPage'));

const UploadedImagesPage = lazy(() => import(/* webpackChunkName: "uploadedImages" */'@/pages/UploadedImagesPage'));

const adminPath = import.meta.env.VITE_ADMIN_URL_PATH;

const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
);

// Legacy-bookmark redirects under /dashboard/:orgId must resolve to an
// absolute, org-qualified path rather than a bare relative <Navigate>. A
// relative target (e.g. `to="workflows/tasks"`) misresolves to the app root
// on a hard page load — the auth context's org-switch re-render appears to
// remount these routes with a stale relative base, sending the browser to
// the top-level `*` catch-all (-> marketing home) instead of the intended
// page. Confirmed by reproducing on the pre-existing `stores` redirect,
// which had the same bug before this component existed.
function OrgRedirect({ to }: { to: string }) {
  const { orgId } = useParams<{ orgId: string }>();
  return <Navigate to={`/dashboard/${orgId}/${to}`} replace />;
}

export const createRoutes = () => [
  // Marketing/Landing pages
  {
    path: '/',
    element: <LazyWrapper><MarketingLandingPage /></LazyWrapper>,
  },
  {
    path: '/slideshow',
    element: <LazyWrapper><SlideshowPage /></LazyWrapper>,
  },
  {
    path: `${adminPath}/orgs`,
    element: <Navigate to="/client/orgs" />,
  },
  {
    path: '/uploaded',
    element: <LazyWrapper><UploadedImagesPage /></LazyWrapper>,
  },

  // Client auth routes (login, orgs selection)
  {
    path: '/client',
    element: <ClientApp />,
    children: [
      {
        index: true,
        element: <Navigate to="login" replace />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'orgs',
        element: (
          <ProtectedRoute>
            <OrgsPage />
          </ProtectedRoute>
        ),
      },
      // Legacy redirect: /client/dashboard/:orgId/* -> /dashboard/:orgId/*
      {
        path: 'dashboard/:orgId/*',
        element: (
          <ProtectedRoute requireOrg>
            <LegacyClientRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <Navigate to="/client/login" replace />,
      },
    ],
  },

  // Unified Dashboard routes
  {
    path: '/dashboard/:orgId',
    element: (
      <ProtectedRoute requireOrg>
        <UnifiedDashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      // Default redirect to first available feature
      {
        index: true,
        element: <DefaultFeatureRedirect />,
      },

      // Client features (with FeatureGuard)
      {
        path: 'reports',
        element: (
          <FeatureGuard feature="reports">
            <ClientReportsPage />
          </FeatureGuard>
        ),
      },
      {
        path: 'team',
        element: (
          <FeatureGuard feature="team">
            <ClientTeamPage />
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows',
        element: (
          <FeatureGuard feature="workflows">
            <WorkflowsRouteWrapper />
          </FeatureGuard>
        ),
      },
      {
        // Superseded by the unified Tasks page's Tab 1 (#681/#721 Phase 7d) —
        // keep old bookmarks working.
        path: 'workflows/active',
        element: <OrgRedirect to="workflows/tasks" />,
      },
      {
        // Unified Tasks page (#681/#721), linked from the nav as of Phase 7d.
        // Ambient workflows view like Templates, since Tab 1 now includes
        // plain SOP bots too, not just instant tasks.
        path: 'workflows/tasks',
        element: (
          <FeatureGuard feature="workflows">
            <TasksHomePage />
          </FeatureGuard>
        ),
      },
      {
        // The standalone AI page is retired; Build with AI is a panel in the editor,
        // whose own guards apply once the redirect lands there
        path: 'workflows/ai',
        element: <LegacyAIRedirect />,
      },
      {
        // The workflow guide and Ask mode: any workflows permission plus the org's
        // ai_chat feature, which the page checks (docs/workflow-ai-agent.md §8)
        path: 'workflows/guide',
        element: (
          <FeatureGuard feature="workflows">
            <LazyWrapper><GuidePage /></LazyWrapper>
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows/:botId/data',
        element: (
          <FeatureGuard feature="workflows">
            <TasksPage />
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows/:botId/data/:groupId/view',
        element: (
          <FeatureGuard feature="workflows">
            <TaskGroupViewPage />
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows/:botId/data/:groupId/edit',
        element: (
          <FeatureGuard feature="workflows">
            <TaskGroupDataPage />
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows/:botCode/execute',
        element: (
          <FeatureGuard feature="workflows">
            <WorkflowPage />
          </FeatureGuard>
        ),
      },
      {
        // The Store feature was folded into Master Data — keep old bookmarks working.
        path: 'stores',
        element: <OrgRedirect to="master-data?dataset=store" />,
      },
      {
        // Superseded by the unified Tasks page's Tab 1 (#681/#721 Phase 7d) —
        // keep old bookmarks working. Instant-task creation is still gated by
        // view_instant, just inside the Tasks page's create-task button now.
        path: 'workflows/instant',
        element: <OrgRedirect to="workflows/tasks" />,
      },
      {
        // Was events -> workflows/instant; repointed straight to
        // workflows/tasks now that workflows/instant is itself just a
        // redirect (#681/#721 Phase 7d) — avoids a double-hop.
        path: 'events',
        element: <OrgRedirect to="workflows/tasks" />,
      },
      // Workflow create/edit — feature-guarded with granular action permissions
      {
        path: 'create-workflow-v4',
        element: (
          <FeatureGuard feature="workflows" action="add">
            <CreateChatbotV3 />
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows/:chatbotId/edit-v4',
        element: (
          <FeatureGuard feature="workflows" action="edit">
            <EditChatbotV4 />
          </FeatureGuard>
        ),
      },
      {
        path: 'workflows/:chatbotId/tasks',
        element: (
          <AdminGuard>
            <LazyWrapper><TaskApprovalPage /></LazyWrapper>
          </AdminGuard>
        ),
      },
      {
        path: 'nudges',
        element: (
          <FeatureGuard feature="nudges">
            <LazyWrapper><CalendarPage /></LazyWrapper>
          </FeatureGuard>
        ),
      },

      // Delivery logs - Admin + Super Admin
      {
        path: 'delivery-logs',
        element: (
          <AdminGuard>
            <DeliveryLogsPage />
          </AdminGuard>
        ),
      },

      // Metabase reports - Super Admin only
      {
        path: 'metabase-reports',
        element: (
          <AdminGuard>
            <MetabaseReportsPage />
          </AdminGuard>
        ),
      },

      // Instant task Flow - Super Admin only; the same deployment-wide Flow under any org
      {
        path: 'instant-flow',
        element: (
          <AdminGuard>
            <InstantFlowPage />
          </AdminGuard>
        ),
      },

      // Bulk QR codes - Super Admin only
      {
        path: 'bulk-qr-codes',
        element: (
          <AdminGuard>
            <BulkQrCodesPage />
          </AdminGuard>
        ),
      },

      {
        path: 'master-data',
        element: (
          <FeatureGuard feature="master_data">
            <MasterDataPage />
          </FeatureGuard>
        ),
      },

      // Profiling routes - Super Admin only
      {
        path: 'profiling',
        element: (
          <AdminGuard>
            <ProfilingPage />
          </AdminGuard>
        ),
      },
      {
        path: 'profiling/profiles',
        element: (
          <AdminGuard>
            <ProfileListPage />
          </AdminGuard>
        ),
      },
      {
        path: 'profiling/profiles/:profileId',
        element: (
          <AdminGuard>
            <ProfileDetailPage />
          </AdminGuard>
        ),
      },
      {
        path: 'profiling/settings',
        element: (
          <AdminGuard>
            <ProfilingSettingsPage />
          </AdminGuard>
        ),
      },
    ],
  },

  // Industry landing pages
  {
    path: '/industries/:industry',
    element: <LazyWrapper><IndustryLandingPage /></LazyWrapper>,
  },

  // Product landing pages
  {
    path: '/whatsapp-task-management',
    element: <LazyWrapper><InstantTasksLandingPage /></LazyWrapper>,
  },
  // Retired slug. The server answers this with a 301 (see http/seo.go), so this
  // route only catches in-app navigation that never hits the network.
  {
    path: '/products/instant-tasks',
    element: <Navigate to="/whatsapp-task-management" replace />,
  },

  // Static pages
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/team',
    element: <TeamPage />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },

  // Catch-all redirect
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];
